import React, { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowUpFromLine, Calendar, ChevronLeft, ChevronRight, X,
  History, Plus, TrendingUp, Clock, CheckCircle2, Sparkles,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { format } from "date-fns";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { createMerchantWithdrawal, getMerchantWithdrawals } from "@/shared/api/services/merchantService";

/* ─────────────────────────── STYLES ─────────────────────────── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');

.mw-root {
  font-family: 'DM Sans', sans-serif;
  min-height: 100vh;
  padding-bottom: 60px;
  --cyan: #22d3ee;
  --cyan-dim: rgba(34,211,238,0.1);
  --surface: rgba(8,16,38,0.96);
  --surface-2: rgba(11,22,50,0.95);
  --border: rgba(34,211,238,0.1);
  --border-hover: rgba(34,211,238,0.3);
  --text-1: #e8f4ff;
  --text-2: #7aaec8;
  --text-3: #3a5a78;
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}
.mw-root * { box-sizing: border-box; }

/* ── Page title ── */
.mw-h1 {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.85rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: #eaf6ff;
}
.mw-sub { 
  color: #eaf6ff;       /* light blue like mw-h1 */
  font-family: 'DM Sans', sans-serif;
  font-size: 13px; 
  margin-top: 5px; 
  font-weight: 500; 
}

/* ── Icon ring ── */
.mw-icon-ring {
  width: 50px; height: 50px; border-radius: 16px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  box-shadow: 0 10px 32px rgba(6,182,212,0.45);
  position: relative; overflow: hidden;
}
.mw-icon-ring::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(145deg, rgba(255,255,255,0.15), transparent);
}

/* ── Stat card ── */
.mw-stat {
  background: linear-gradient(145deg, var(--surface), var(--surface-2));
  border: 1px solid var(--border);
  border-radius: 20px; padding: 18px 20px;
  display: flex; align-items: center; gap: 14px;
  position: relative; overflow: hidden;
  cursor: default;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s var(--spring);
  will-change: transform;
}
.mw-stat::before {
  content: '';
  position: absolute; top: 0; left: 20%; right: 20%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent);
}
.mw-stat:hover {
  border-color: rgba(34,211,238,0.22);
  box-shadow: 0 0 50px rgba(34,211,238,0.08), 0 16px 48px rgba(0,0,0,0.35);
  transform: translateY(-4px);
}
.mw-stat-icon {
  width: 44px; height: 44px; border-radius: 14px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  position: relative;
}
.mw-stat-icon::after {
  content: ''; position: absolute; inset: 0; border-radius: 14px;
  background: rgba(255,255,255,0.08);
}
.mw-stat-lbl { color: var(--text-3); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; }
.mw-stat-val {
  color: var(--text-1); font-size: 1.25rem; font-weight: 800;
  font-family: 'JetBrains Mono', monospace; margin-top: 3px; letter-spacing: -0.02em;
}

/* ── Tabs ── */
.mw-tabs-list {
  display: inline-flex; gap: 4px;
  background: rgba(8,16,38,0.95);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 18px; padding: 5px;
}
.mw-tab {
  border-radius: 13px; padding: 10px 24px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  border: none; display: flex; align-items: center; gap: 8px;
  font-family: 'DM Sans', sans-serif;
  color: var(--text-3); background: transparent;
  transition: all 0.3s var(--spring);
  position: relative; overflow: hidden;
}
.mw-tab:hover { color: var(--text-2); background: rgba(34,211,238,0.05); }
.mw-tab.active {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  color: #fff;
  box-shadow: 0 6px 22px rgba(6,182,212,0.45);
  transform: translateY(-1px);
}
.mw-tab.active::before {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(145deg, rgba(255,255,255,0.13), transparent);
}

/* ── Filter bar ── */
.mw-filter {
  background: linear-gradient(145deg, var(--surface), var(--surface-2));
  border: 1px solid var(--border);
  border-radius: 18px; padding: 16px 20px;
  display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
  margin-bottom: 16px; position: relative; overflow: hidden;
}
.mw-filter::before {
  content: '';
  position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.22), transparent);
}

/* ── Select ── */
.mw-select {
  background: rgba(34,211,238,0.04) !important;
  border: 1px solid rgba(34,211,238,0.12) !important;
  color: var(--text-1) !important; border-radius: 12px !important;
  min-width: 150px; font-family: 'DM Sans', sans-serif !important;
  transition: all 0.25s ease !important;
}
.mw-select:hover {
  border-color: rgba(34,211,238,0.3) !important;
  box-shadow: 0 0 18px rgba(34,211,238,0.1) !important;
}

/* ── Date btn ── */
.mw-date-btn {
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.12);
  border-radius: 12px; padding: 10px 16px; color: var(--text-3);
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 8px;
  font-family: 'DM Sans', sans-serif; white-space: nowrap;
  transition: all 0.25s var(--spring);
}
.mw-date-btn:hover {
  border-color: rgba(34,211,238,0.35); color: var(--cyan);
  box-shadow: 0 0 18px rgba(34,211,238,0.12); transform: translateY(-1px);
}

/* ── Clear btn ── */
.mw-clear-btn {
  background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.18);
  border-radius: 12px; padding: 10px 16px; color: #fca5a5;
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 7px;
  font-family: 'DM Sans', sans-serif;
  transition: all 0.22s ease;
}
.mw-clear-btn:hover {
  border-color: rgba(239,68,68,0.38); background: rgba(239,68,68,0.1); transform: translateY(-1px);
}

/* ── Pagination ── */
.mw-pag-btn {
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.12);
  border-radius: 10px; padding: 8px 12px; color: var(--text-3);
  cursor: pointer; display: flex; align-items: center;
  transition: all 0.25s var(--spring);
}
.mw-pag-btn:hover:not(:disabled) {
  border-color: rgba(34,211,238,0.35); color: var(--cyan);
  box-shadow: 0 0 16px rgba(34,211,238,0.15); transform: translateY(-1px);
}
.mw-pag-btn:disabled { opacity: 0.3; cursor: not-allowed; }

/* ── Responsive ── */
@media (max-width: 640px) {
  .mw-stats-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
  .mw-h1 { font-size: 1.5rem; }
  .mw-tab { padding: 9px 14px; font-size: 12px; }
  .mw-filter { padding: 12px 14px; }
}
@media (max-width: 400px) {
  .mw-stats-grid { grid-template-columns: 1fr !important; }
}
`;

/* ─────────────── STAT CARD ─────────────── */
const StatCard = ({ icon: Icon, gradient, label, value, delay = 0 }) => (
  <motion.div
    className="mw-stat"
    initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -4 }}
  >
    <div className="mw-stat-icon" style={{ background: gradient }}>
      <Icon size={20} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
    </div>
    <div>
      <div className="mw-stat-lbl">{label}</div>
      <motion.div
        className="mw-stat-val"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: delay + 0.12, type: "spring", stiffness: 400, damping: 22 }}
      >
        {value}
      </motion.div>
    </div>
  </motion.div>
);

/* ─────────────── MAIN ─────────────── */
export default function MerchantWithdrawals() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const { data: withdrawals = [], isLoading } = useQuery({
    queryKey: ["merchant-withdrawals"],
    queryFn: getMerchantWithdrawals,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const transactions = React.useMemo(() =>
    withdrawals.map((w) => ({
      id: w.id, transaction_id: w.id, type: "withdrawal",
      amount: typeof w.amount === "string" ? parseFloat(w.amount) : w.amount,
      status: (w.status || "").toLowerCase(),
      created_date: w.createdAt, provider_display: w.bank_name,
    })), [withdrawals]);

  const filteredTransactions = React.useMemo(() =>
    transactions.filter((tx) => {
      if (statusFilter !== "all" && tx.status !== statusFilter) return false;
      if (dateRange.from && new Date(tx.created_date) < dateRange.from) return false;
      if (dateRange.to && new Date(tx.created_date) > dateRange.to) return false;
      return true;
    }), [transactions, statusFilter, dateRange]);

  const paginatedTransactions = filteredTransactions.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const stats = React.useMemo(() => {
    const success = transactions.filter(t => t.status === "success");
    const pending = transactions.filter(t => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((s, t) => s + (t.amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((s, t) => s + (t.amount || 0), 0),
    };
  }, [transactions]);

  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    try {
      let input;
      if (data.type === "bank") {
        input = { amount: data.amount, bank_code: data.bank_code, bank_account: data.account_number, account_name: data.account_name, bank_name: data.bank_name };
      } else if (data.type === "usdt") {
        input = { amount: data.amount, network: data.network, wallet_address: data.wallet_address };
      } else throw new Error("Invalid withdrawal type");
      await createMerchantWithdrawal(input);
      await queryClient.invalidateQueries({ queryKey: ["merchant-withdrawals"] });
      toast.success(t("withdrawalRequestSubmittedSuccessfully"));
    } catch (error) {
      toast.error(error?.message || t("somethingWentWrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => { setStatusFilter("all"); setDateRange({ from: null, to: null }); setPage(0); };
  const hasFilters = statusFilter !== "all" || dateRange.from;

  const ddStyle = {
    background: "rgba(8,16,38,0.98)",
    border: "1px solid rgba(34,211,238,0.15)",
    borderRadius: 14,
    color: "#e8f4ff",
    fontFamily: "'DM Sans', sans-serif",
    boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
    backdropFilter: "blur(20px)",
  };

  return (
    <MerchantLayout currentPage="MerchantWithdrawals" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{STYLE}</style>

      <div className="mw-root">
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* ── Header ── */}
          <motion.div
            initial={{ opacity: 0, y: -18, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: "flex", alignItems: "center", gap: 16 }}
          >
            <div className="mw-icon-ring">
              <ArrowUpFromLine size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
            </div>
            <div>
              <h1 className="mw-h1">{t("withdrawals")}</h1>
              <p className="mw-sub">Request payouts via Bank Transfer or USDT</p>
            </div>
          </motion.div>

          {/* ── Stats ── */}
          <div className="mw-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
            <StatCard icon={TrendingUp} gradient="linear-gradient(135deg,#06b6d4,#0891b2)" label={t("totalWithdrawals")} value={`฿${stats.total.toLocaleString()}`} delay={0} />
            <StatCard icon={Clock} gradient="linear-gradient(135deg,#f59e0b,#d97706)" label={t("pending")} value={stats.pendingCount} delay={0.07} />
            <StatCard icon={CheckCircle2} gradient="linear-gradient(135deg,#10b981,#059669)" label="Pending Amount" value={`฿${stats.pendingAmount.toLocaleString()}`} delay={0.14} />
          </div>

          {/* ── Tabs ── */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="mw-tabs-list">
              {[
                { id: "new", icon: Plus, label: t("newWithdrawal") },
                { id: "history", icon: History, label: t("history") },
              ].map(({ id, icon: Icon, label }) => (
                <motion.button
                  key={id}
                  className={`mw-tab${activeTab === id ? " active" : ""}`}
                  onClick={() => setActiveTab(id)}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon size={15} />
                  {label}
                </motion.button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {/* ── New Withdrawal ── */}
              {activeTab === "new" && (
                <motion.div
                  key="new"
                  initial={{ opacity: 0, y: 20, filter: "blur(5px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -14, filter: "blur(5px)" }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  style={{ marginTop: 22 }}
                >
                  <WithdrawalForm onSubmit={handleWithdrawalSubmit} submitting={submitting} stats={stats} accentColor="emerald" />
                </motion.div>
              )}

              {/* ── History ── */}
              {activeTab === "history" && (
                <motion.div
                  key="history"
                  initial={{ opacity: 0, y: 20, filter: "blur(5px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -14, filter: "blur(5px)" }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  style={{ marginTop: 22 }}
                >
                  {/* Filter bar */}
                  <motion.div className="mw-filter" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                      <SelectTrigger className="mw-select" style={{ width: 155, height: 40 }}>
                        <SelectValue placeholder={t("status")} />
                      </SelectTrigger>
                      <SelectContent style={ddStyle}>
                        <SelectItem value="all">{t("allStatus")}</SelectItem>
                        <SelectItem value="success">{t("success")}</SelectItem>
                        <SelectItem value="pending">{t("pending")}</SelectItem>
                        <SelectItem value="processing">{t("processing")}</SelectItem>
                        <SelectItem value="failed">{t("failed")}</SelectItem>
                      </SelectContent>
                    </Select>

                    <Popover>
                      <PopoverTrigger asChild>
                        <motion.button className="mw-date-btn" whileTap={{ scale: 0.97 }}>
                          <Calendar size={14} />
                          {dateRange.from
                            ? dateRange.to
                              ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d")}`
                              : format(dateRange.from, "MMM d")
                            : t("dateRange")}
                        </motion.button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start" style={{ background: "rgba(8,16,38,0.98)", border: "1px solid rgba(34,211,238,0.15)", borderRadius: 18, backdropFilter: "blur(24px)" }}>
                        <CalendarComponent mode="range" selected={dateRange} onSelect={(r) => { setDateRange(r || { from: null, to: null }); setPage(0); }} numberOfMonths={2} />
                      </PopoverContent>
                    </Popover>

                    <AnimatePresence>
                      {hasFilters && (
                        <motion.button className="mw-clear-btn" onClick={clearFilters}
                          initial={{ opacity: 0, scale: 0.8, x: -8 }}
                          animate={{ opacity: 1, scale: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.8, x: -8 }}
                          transition={{ duration: 0.2 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <X size={13} /> {t("clear")}
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  <TransactionTable transactions={paginatedTransactions} isLoading={isLoading} onRowClick={setSelectedTransaction} />

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <motion.div
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 18, flexWrap: "wrap", gap: 12 }}
                    >
                      <p style={{ color: "#3a5a78", fontSize: 13, fontWeight: 500 }}>
                        {t("showing")} {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filteredTransactions.length)} {t("of")} {filteredTransactions.length}
                      </p>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <motion.button className="mw-pag-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0} whileTap={page > 0 ? { scale: 0.94 } : {}}>
                          <ChevronLeft size={16} />
                        </motion.button>
                        <span style={{ color: "#5a8aaa", fontSize: 13, fontWeight: 600, padding: "0 8px" }}>
                          {page + 1} / {totalPages}
                        </span>
                        <motion.button className="mw-pag-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} whileTap={page < totalPages - 1 ? { scale: 0.94 } : {}}>
                          <ChevronRight size={16} />
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      <TransactionDetailPanel transaction={selectedTransaction} open={!!selectedTransaction} onClose={() => setSelectedTransaction(null)} />
    </MerchantLayout>
  );
}